import React from 'react';
import Style from './Governance.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits across the three Governance control types (DATA / WEB / AI).
// Control-type routing rides a URL query param `?type=WEB|AI|DATA` (default DATA)
// so no App.js routes / nav / globalViewDisabled changes are needed. Both the
// landing and editor frames read the type from location.search and dispatch to
// the matching control-type body.
// ─────────────────────────────────────────────────────────────────────────────

export const VALID_TYPES = ['DATA', 'WEB', 'AI', 'EXTENSION'];

// Per-control-type copy — the frames read title/subtitle/labels from here instead
// of hardcoding "Data Controls". DATA keeps its exact existing strings.
export const CONTROL_META = {
  DATA: {
    title: 'Data Controls',
    subtitle: 'Configure global DLP detection rules for sensitive data and file transfer restrictions.',
    editLabel: 'Edit Data Controls Policy',
    createLabel: 'Create Policy',
    editorTitle: 'Edit Data Controls Policy',
    returnLabel: 'Return to Data Controls',
  },
  WEB: {
    title: 'Web Controls',
    subtitle: 'Define which sites and SaaS applications are sanctioned or denied — with per-app data controls and conditional access.',
    editLabel: 'Edit Web Controls Policy',
    createLabel: 'Create Policy',
    editorTitle: 'Edit Web Controls Policy',
    returnLabel: 'Return to Web Controls',
  },
  AI: {
    title: 'AI Controls',
    subtitle: 'Govern GenAI applications — per-app prompt / response data controls and conditional access.',
    editLabel: 'Edit AI Controls Policy',
    createLabel: 'Create Policy',
    editorTitle: 'Edit AI Controls Policy',
    returnLabel: 'Return to AI Controls',
  },
  EXTENSION: {
    title: 'Extension Controls',
    // States the cross-browser promise, which is the whole point of the feature:
    // policy is written once against a logical extension and applies wherever that
    // extension appears, regardless of the different id each browser assigns it.
    subtitle: 'Govern browser extensions across Chrome, Firefox and Edge — one rule per extension, applied wherever it is found.',
    // No edit/create labels: extension rule DEFINITIONS are seeded, read-only
    // database rows. There is no editor to enter and no policy to create from the
    // portal — a PixmAdmin only attaches one of the predefined rules.
    editLabel: null,
    createLabel: null,
    editorTitle: null,
    returnLabel: 'Return to Extension Controls',
  },
};

// Tab bar — WEB/AI/EXTENSION are enabled; ALERT stays "Soon".
export const TABS = [
  { key: 'WEB',       label: 'Web Controls',       enabled: true },
  { key: 'AI',        label: 'AI Controls',        enabled: true },
  { key: 'DATA',      label: 'Data Controls',      enabled: true },
  { key: 'EXTENSION', label: 'Extension Controls', enabled: true },
  { key: 'ALERT',     label: 'Alert Config',       enabled: false },
];

// Version-status → badge.
export const STATUS_BADGE = {
  DRAFT:     { label: 'Draft',     cls: 'moderate' },
  APPROVED:  { label: 'Approved',  cls: 'low' },
  PUBLISHED: { label: 'Published', cls: 'low' },
  OBSOLETE:  { label: 'Obsolete',  cls: 'neutral' },
  REJECTED:  { label: 'Rejected',  cls: 'critical' },
};

// App disposition → badge (matches the mockup: sanctioned green, unsanctioned
// amber, denied red).
export const DISPOSITION_BADGE = {
  SANCTIONED:   { label: 'Sanctioned',   cls: 'low' },
  UNSANCTIONED: { label: 'Unsanctioned', cls: 'moderate' },
  DENIED:       { label: 'Denied',       cls: 'critical' },
};

// DLP action → { label, cls }. "DEFAULT" = inherit the DATA baseline (no row stored).
export const ACTION_META = {
  DEFAULT: { label: 'Default', cls: 'neutral' },
  IGNORE:  { label: 'Ignore',  cls: 'neutral' },
  REPORT:  { label: 'Report',  cls: 'low' },
  WARN:    { label: 'Warn',    cls: 'moderate' },
  BLOCK:   { label: 'Block',   cls: 'critical' },
};

export const ACTION_OPTIONS = ['DEFAULT', 'IGNORE', 'REPORT', 'WARN', 'BLOCK'];
// The AI RESPONSE channel is API-restricted to Ignore/Report (Warn/Block rejected).
export const RESPONSE_ACTION_OPTIONS = ['DEFAULT', 'IGNORE', 'REPORT'];

// Sensitive-data category → display label + ordering (mirrors DataControls).
export const CATEGORY_LABEL = {
  FINANCIAL:     'Financial',
  GOVERNMENT_ID: 'Government IDs',
  CREDENTIALS:   'Credentials & Secrets',
  PII:           'PII',
  CUSTOM:        'Custom',
};
export const CATEGORY_ORDER = ['FINANCIAL', 'GOVERNMENT_ID', 'CREDENTIALS', 'PII', 'CUSTOM'];

// Built-in scan-rule code → category (same mapping the API uses to seed the DATA
// baseline). Lets the override form group the scan-rule master by category
// without depending on a published DATA policy. Unknown codes fall to CUSTOM.
export const SCAN_RULE_CATEGORY = {
  SR_CC_NUMBERS:     'FINANCIAL',
  SR_ABA_ROUTING:    'FINANCIAL',
  SR_US_SSN:         'GOVERNMENT_ID',
  SR_US_ITIN:        'GOVERNMENT_ID',
  SR_US_EIN:         'GOVERNMENT_ID',
  SR_PRIVATE_KEY:    'CREDENTIALS',
  SR_AWS_ACCESS_KEY: 'CREDENTIALS',
  SR_EMAIL_ADDRESS:  'PII',
};

// ── WEB / AI "policy = site" model ──────────────────────────────────────────
// App disposition options offered when adding/editing a site.
export const SITE_DISPOSITIONS = [
  { code: 'SANCTIONED',   label: 'Sanctioned' },
  { code: 'UNSANCTIONED', label: 'Unsanctioned' },
  { code: 'DENIED',       label: 'Denied' },
];
// Only SANCTIONED sites carry per-rule edits + conditional-access email domains;
// UNSANCTIONED ships the global defaults read-only; DENIED is blocked outright and
// shows no rule tables at all (the extension refuses to load the page for it).
export const DISPOSITION_SANCTIONED = 'SANCTIONED';
export const DISPOSITION_DENIED = 'DENIED';

// The rule family + scan-rule codes for the two system-managed domain-check rules.
// They ride the heartbeat but are HIDDEN from every UI rule table (they carry the
// site's own domain/email metadata, not a data-control the admin edits).
export const RULE_FAMILY_DOMAIN = 'DOMAIN';
// Family of the single attached extension rule.
export const RULE_FAMILY_BROWSER_EXTENSION = 'BROWSER_EXTENSION';
// The default "Any other extension" rule. It matches every extension
// unconditionally and resolves to Unsanctioned, which is what makes the incident
// feed report Allowed, Denied AND Unsanctioned. Only a PixmAdmin may detach it.
export const SR_EXT_ANY = 'SR_EXT_ANY';
// Every hidden scan rule. The extension rules join the domain-check pair here so
// they never render as editable rows in a rule table: like SR_DOMAIN_CHECK, they are
// named hooks whose condition tree is a placeholder, with the values they match
// against supplied separately.
export const HIDDEN_SCAN_RULE_CODES = new Set([
  'SR_DOMAIN_CHECK', 'SR_EMAIL_DOMAIN_CHECK',
]);
// Matched by prefix rather than enumerated: the seeded set grows by database
// release (ten per-extension DENY rules today), and listing them here would mean a
// portal change every time one is added.
export const isExtensionScanRuleCode = (code) => typeof code === 'string' && code.startsWith('SR_EXT_');

// Is this a hidden domain-check rule row? A draft rule DTO carries only
// ruleFamilyCode (DOMAIN); a published rule DTO also carries scanRuleCode — match
// on either so the filter works from both the draft and published read paths.
// A rule the portal must never render in an editable rule table.
export const isHiddenRuleCode = (code) =>
  HIDDEN_SCAN_RULE_CODES.has(code) || isExtensionScanRuleCode(code);

export const isHiddenSiteRule = (rule) =>
  !!rule && (rule.ruleFamilyCode === RULE_FAMILY_DOMAIN ||
    (rule.scanRuleCode && HIDDEN_SCAN_RULE_CODES.has(rule.scanRuleCode)));

// Concrete (non-"Default") action options for the copied site rules. Unlike the
// old override form there is no "inherit the baseline" — every copied rule carries
// a real action. AI RESPONSE is API-restricted to Ignore/Report.
export const SITE_ACTION_OPTIONS = ['IGNORE', 'REPORT', 'WARN', 'BLOCK'];
export const SITE_RESPONSE_ACTION_OPTIONS = ['IGNORE', 'REPORT'];

// Strip the '{WEB|AI}-' prefix a site PolicyName carries, so the landing can still
// show a domain when a site row has no app hydrated (defensive; normally appDomain
// is present).
export const domainFromPolicyName = (policyName) =>
  typeof policyName === 'string' ? policyName.replace(/^(WEB|AI)-/i, '') : '';

// Normalize a user-entered site domain to a clean bare host — MUST mirror the
// backend `normalizeDomain` (internal-portal-pixm-fa/governance/lib.js) so what the
// UI validates/sends matches what the server derives the PolicyName from and stores.
// Accepts a URL or bare domain: strips scheme, leading www., path/query/fragment, port.
export const normalizeDomain = (domain) => {
  if (!domain) return '';
  let d = String(domain).trim().toLowerCase();
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // strip scheme
  d = d.replace(/^www\./, '');                  // strip leading www.
  d = d.split('/')[0].split('?')[0].split('#')[0]; // strip path/query/fragment
  d = d.split(':')[0];                          // strip port
  return d.trim();
};

// Standard domain validation on the ALREADY-normalized host — mirrors the backend
// `isValidDomain`. Dot-separated labels (1-63 alnum/hyphen chars, no leading/trailing
// hyphen), a real TLD (2-63 alphabetic or a punycode `xn--` TLD), total <= 253.
// Lookbehind-free for browser support.
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(xn--[a-z0-9]{2,59}|[a-z]{2,63})$/i;
export const isValidDomain = (domain) => typeof domain === 'string' && DOMAIN_RE.test(domain);

// Version statuses in which rule/app content is frozen (no add/edit/delete). A
// DRAFT or a REJECTED draft is editable; APPROVED/PUBLISHED/OBSOLETE are not.
export const NON_EDITABLE_STATUSES = new Set(['APPROVED', 'PUBLISHED', 'OBSOLETE']);

// Derive the working-draft meta from a draft object, tolerating both PascalCase
// (SQL view) and camelCase (DTO) field casings. Shared by both editors so the
// draft-lifecycle derivation lives in exactly one place.
export const draftMetaOf = (draft) => {
  const version = draft && draft.version ? draft.version : null;
  const draftStatus = version ? (version.StatusCode || version.statusCode || null) : null;
  return {
    draftExists: !!version,
    draftVersion: version,
    policyVersionId: version ? (version.PolicyVersionId ?? version.policyVersionId) : null,
    draftStatus,
    isEditable: !NON_EDITABLE_STATUSES.has(draftStatus),
  };
};

// Read the active control type from a location.search string (default DATA).
export const controlTypeFromSearch = (search) => {
  try {
    const t = new URLSearchParams(search || '').get('type');
    const up = (t || '').toUpperCase();
    return VALID_TYPES.includes(up) ? up : 'DATA';
  } catch (e) {
    return 'DATA';
  }
};

// Append/replace ?type= on a base path, omitting it for DATA to keep the legacy URL.
export const withType = (basePath, controlType) =>
  controlType && controlType !== 'DATA' ? `${basePath}?type=${controlType}` : basePath;

// Build the editor URL for a control type (+ optional site policyId [+ optional
// preset disposition]). WEB/AI sites are addressed by ?policyId=; a missing
// policyId is the editor's "create a new site" (create mode). DATA keeps its
// legacy /edit URL (no query params).
// `presetDisposition` (SANCTIONED|DENIED) is PIXMC-6083's Sanction/Deny deep-link
// (§10.3, D4): every discovered domain already has an UNSANCTIONED site, so those
// two buttons open the SAME existing site's editor via policyId — this is the only
// way to tell the editor which one was clicked, so it can arrive with that
// disposition pre-selected instead of showing "Unsanctioned". Omit it (every other
// caller — Governance.jsx's Edit, SiteCreatorBase's post-create redirect — does)
// and the editor seeds from the server's real disposition exactly as before.
export const editorPath = (customerId, controlType, policyId, presetDisposition, logicalExtensionId) => {
  const base = `/customers/${customerId}/governance/edit`;
  const params = [];
  if (controlType && controlType !== 'DATA') params.push(`type=${controlType}`);
  if (policyId) params.push(`policyId=${encodeURIComponent(policyId)}`);
  if (presetDisposition) params.push(`presetDisposition=${encodeURIComponent(presetDisposition)}`);
  // EXTENSION attach only: pre-select this extension (and its predefined rule) in the editor,
  // instead of making the admin pick it again. Only meaningful in create/attach mode (no policyId);
  // every other caller omits it and the editor opens with an empty rule picker exactly as before.
  if (logicalExtensionId) params.push(`logicalExtensionId=${encodeURIComponent(logicalExtensionId)}`);
  return params.length ? `${base}?${params.join('&')}` : base;
};

// Read the ?policyId= site identifier from a location.search string (null = the
// editor is in create mode).
export const policyIdFromSearch = (search) => {
  try {
    return new URLSearchParams(search || '').get('policyId') || null;
  } catch (e) {
    return null;
  }
};

// Read the ?logicalExtensionId= pre-selection from a location.search string (null = no
// pre-selection; the editor's rule picker opens empty). Used only by the Extension editor's
// attach mode to auto-select the extension the admin clicked "Attach Rule" on.
export const logicalExtensionIdFromSearch = (search) => {
  try {
    return new URLSearchParams(search || '').get('logicalExtensionId') || null;
  } catch (e) {
    return null;
  }
};

// Read the ?presetDisposition= override from a location.search string. Allow-listed
// to SANCTIONED/DENIED — UNSANCTIONED is already what the editor seeds by default,
// and rejecting anything else keeps a malformed/stray query param from doing
// anything (falls through to the normal server-seeded disposition).
const PRESET_DISPOSITIONS = new Set([DISPOSITION_SANCTIONED, DISPOSITION_DENIED]);
export const presetDispositionFromSearch = (search) => {
  try {
    const v = (new URLSearchParams(search || '').get('presetDisposition') || '').toUpperCase();
    return PRESET_DISPOSITIONS.has(v) ? v : null;
  } catch (e) {
    return null;
  }
};

// Small presentational badges reused by the landing + tables.
export const VersionBadge = ({ statusCode, versionNumber }) => {
  const s = STATUS_BADGE[statusCode] || { label: statusCode, cls: 'neutral' };
  return (
    <span className={`${Style.badge} ${Style[s.cls]}`} style={{ marginLeft: 10 }}>
      {versionNumber ? `v${versionNumber} · ` : ''}{s.label}
    </span>
  );
};

export const DispositionBadge = ({ code }) => {
  const d = DISPOSITION_BADGE[code] || { label: code, cls: 'neutral' };
  return <span className={`${Style.badge} ${Style[d.cls]}`}>{d.label}</span>;
};
